'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
import NavBar from '@/components/header/NavBar';
import { useLocale } from 'next-intl';
import { GlowingEffect } from '@/components/home-page/glowing-effect';
import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";
import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';

const ContactForms: React.FC = () => {

  return (<>
  {/* Fixed background — stays behind everything while page scrolls */}
  <div
    className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
    style={{ backgroundImage: `url('/hero/construction.avif')` }}
  >
   
<div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px] " />
  <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4 ">
   Property & Construction
  </h1>
    
  </div>
 

  <NavBar />
   <ul className="grid grid-cols-1 bg-secondary grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 p-8 xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
        icon={<Box className="h-4 w-4 text-black dark:text-primary" />}
        title="Real Estate"
        description="We build property listing sites and search platforms that make it easy for buyers and renters to browse, filter, and inquire about your properties."
      />
 
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
        icon={<Settings className="h-4 w-4 text-black dark:text-primary" />}
        title="Construction & Contractors"
        description="Get a professional web presence that showcases your projects, builds trust with clients, and makes it easy for people to request quotes."
      />
 
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
        icon={<Lock className="h-4 w-4 text-black dark:text-primary" />}
        title="Property Management"
        description="We build secure, reliable digital solutions for property managers, helping streamline tenant communication, protect sensitive data, and deliver seamless experiences for owners and renters."
      />
 
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
        icon={<Sparkles className="h-4 w-4 text-black dark:text-primary" />}
        title="Architecture & Design"
        description="We create high-performing portfolio sites that help architecture and design firms showcase their work, attract clients, and turn online visitors into project inquiries."
      />
 
      <GridItem
        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
        icon={<Search className="h-4 w-4 text-black dark:text-primary" />}
        title="Property Listings & Bookings"
        description="We build smart listing and booking systems that simplify viewings and reservations, manage availability, and create seamless experiences for agents and clients."
      />
    </ul>
<Why/>
<ContactForm/>

<FAQContainer
  title={"Property &\nConstruction FAQs"}
  description="Answers to the most common questions clients ask about our solutions for real estate, construction, and property businesses."
  faq={[
    {
      question: "Can you build a property listing website?",
      answer:
        "Yes, we build listing and search platforms that let buyers or renters browse, filter, and inquire about properties easily.",
    },
    {
      question: "Do you build websites for contractors and construction companies?",
      answer:
        "Yes, we create professional sites that showcase your projects and portfolio, and make it simple for potential clients to request quotes.",
    },
    {
      question: "Can you build a system for managing tenants or properties?",
      answer:
        "Absolutely. We build secure property management solutions that streamline tenant communication and protect sensitive data.",
    },
    {
      question: "Do you design portfolio sites for architects and designers?",
      answer:
        "Yes, we build high-performing portfolio sites that showcase your work and turn visitors into project inquiries.",
    },
    {
      question: "Can you integrate maps or search filters into a listings site?",
      answer:
        "Yes, we integrate map views, location-based search, and advanced filters to make browsing properties fast and intuitive.",
    },
    {
      question: "Do you offer support after the site or system launches?",
      answer:
        "Yes, we offer ongoing maintenance and support plans to keep your listings, booking system, or site running smoothly after launch.",
    },
  ]}
/>
<Footer/>
</>
  );
};

export default ContactForms;


interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
 
const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-[14rem] list-none  rounded-3xl ${area}`}>
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
     <GlowingEffect

  glow={true}
  disabled={false}
  spread={40}
  proximity={64}
  inactiveZone={0.01}
  borderWidth={2}
/>
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 ">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border text-primary border-white p-2">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 mt-8 text-xl  font-bold text-balance text-black md:text-3xl dark:text-primary">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black md:text-xl dark:text-white  font-light">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};